import { useState } from "react";
import { G } from "../constants/theme";
import { Wrap, Sec } from "../components/ui/Layout";
import PageHero from "../components/ui/PageHero";
import { BtnG } from "../components/ui/Buttons";

const CONTACT_ITEMS = [
  { icon: "✉", label: "Email",              val: "info@middleeastblackcarbon.ca" },
  { icon: "◈", label: "Commercial Contact",  val: "Canada" },
  { icon: "⌬", label: "Production Location", val: "Syria" },
  { icon: "◎", label: "Website",             val: "middleeastblackcarbon.ca" },
];

const inputStyle = {
  width: "100%",
  background: G.surface,
  border: `1px solid ${G.border}`,
  color: G.text,
  padding: "13px 15px",
  fontFamily: "sans-serif",
  fontSize: 14,
  outline: "none",
};

const labelStyle = {
  display: "block",
  fontFamily: "monospace",
  fontSize: 9,
  letterSpacing: 2,
  color: G.muted,
  textTransform: "uppercase",
  marginBottom: 7,
};

function Field({ label, name, type = "text", placeholder }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input name={name} type={type} placeholder={placeholder} required={label.includes("*")} style={inputStyle} />
    </div>
  );
}

export default function Contact() {
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus("submitting");

    const formData = new FormData(e.target);

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(formData).toString(),
    })
      .then(() => setStatus("success"))
      .catch(() => setStatus("error"));
  };

  return (
    <>
      <PageHero
        label="Get In Touch"
        title={<>CONTACT OUR<br />COMMERCIAL TEAM</>}
        sub="For technical inquiries, sample requests, bulk supply discussions, or export coordination."
      />

      <Sec bg={G.black}>
        <Wrap>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 64 }}>

            {/* ── Left: contact details ── */}
            <div>
              <div style={{ fontFamily: "monospace", fontSize: 10, letterSpacing: 3, color: G.green,
                textTransform: "uppercase", marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ display: "block", width: 28, height: 1, background: G.green }} />
                Contact Details
              </div>
              <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: G.white,
                letterSpacing: 1, marginBottom: 24 }}>
                REACH OUR TEAM
              </h3>

              {CONTACT_ITEMS.map(({ icon, label, val }) => (
                <div key={label} style={{ display: "flex", gap: 14, alignItems: "flex-start",
                  marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${G.border}` }}>
                  <div style={{ width: 30, height: 30, border: `1px solid ${G.border}`, display: "flex",
                    alignItems: "center", justifyContent: "center", color: G.green, flexShrink: 0, fontSize: 12 }}>
                    {icon}
                  </div>
                  <div>
                    <div style={{ fontFamily: "monospace", fontSize: 9, letterSpacing: 2, color: G.muted,
                      textTransform: "uppercase", marginBottom: 3 }}>
                      {label}
                    </div>
                    <div style={{ fontSize: 12, color: val.includes("@") ? G.green : G.white, wordBreak: "break-all" }}>
                      {val}
                    </div>
                  </div>
                </div>
              ))}

              <div style={{ marginTop: 28, padding: "20px 22px", background: G.surface,
                border: `1px solid ${G.border}`, borderLeft: `2px solid ${G.greenDim}` }}>
                <div style={{ fontFamily: "monospace", fontSize: 9, letterSpacing: 2, color: G.green,
                  textTransform: "uppercase", marginBottom: 8 }}>
                  Response Time
                </div>
                <p style={{ fontSize: 13, color: G.muted, lineHeight: 1.6 }}>
                  We aim to respond to all inquiries within 24–48 hours.
                </p>
              </div>
            </div>

            {/* ── Right: Netlify form ── */}
            <div>
              <div style={{ fontFamily: "monospace", fontSize: 10, letterSpacing: 3, color: G.green,
                textTransform: "uppercase", marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ display: "block", width: 28, height: 1, background: G.green }} />
                Send Inquiry
              </div>

              {status === "success" ? (
                <div style={{ padding: "32px 28px", border: `1px solid ${G.greenDim}`,
                  background: "rgba(76,175,80,.06)" }}>
                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: G.green,
                    letterSpacing: 1, marginBottom: 10 }}>
                    INQUIRY RECEIVED
                  </div>
                  <p style={{ fontSize: 14, color: G.muted, lineHeight: 1.7 }}>
                    Thank you for contacting Middle East Black Carbon. We aim to respond within 24–48 hours.
                  </p>
                </div>
              ) : (
                <form
                  name="inquiry"
                  method="POST"
                  data-netlify="true"
                  netlify-honeypot="bot-field"
                  onSubmit={handleSubmit}
                >
                  {/* Required hidden fields for Netlify */}
                  <input type="hidden" name="form-name" value="inquiry" />
                  <p hidden><input name="bot-field" /></p>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <Field label="Full Name *"       name="name"        placeholder="Your full name" />
                    <Field label="Company Name"      name="company"     placeholder="Your company" />
                    <Field label="Email Address *"   name="email"       type="email" placeholder="you@company.com" />
                    <Field label="Country"           name="country"     placeholder="Country" />
                    <Field label="Required Quantity" name="quantity"    placeholder="e.g. 50 MT" />

                    <div>
                      <label style={labelStyle}>Intended Application</label>
                      <select name="application" style={{ ...inputStyle, appearance: "none" }}>
                        <option value="">Select...</option>
                        <option>Industrial Fillers</option>
                        <option>Plastics &amp; Polymers</option>
                        <option>Rubber Applications</option>
                        <option>Energy / Fuel Recovery</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Message</label>
                    <textarea
                      name="message"
                      placeholder="Describe your requirements, questions, or application details…"
                      style={{ ...inputStyle, height: 110, resize: "vertical" }}
                    />
                  </div>

                  <BtnG type="submit" disabled={status === "submitting"}>
                    {status === "submitting" ? "Sending…" : "Send Inquiry →"}
                  </BtnG>

                  {status === "error" && (
                    <p style={{ marginTop: 12, fontSize: 13, color: "#ef5350", fontFamily: "monospace" }}>
                      Submission failed. Please email us directly at info@middleeastblackcarbon.ca
                    </p>
                  )}
                </form>
              )}
            </div>

          </div>
        </Wrap>
      </Sec>
    </>
  );
}
